import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Statistic, Table, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Line } from '@ant-design/charts';
import type { GroupAnalyticsParams, IapLtvRow } from '@/types';
import { adminApi } from '@services/api';
import { reportLoadErrorMessage } from '@/utils/apiError';

const { Text } = Typography;

const CHECKPOINTS = [0, 1, 3, 7, 14, 30, 60, 90, 180, 365];
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

interface Cohort {
  size: number;
  /** Whole days between the IST signup day and today in IST. */
  age: number;
}

interface CheckpointRow {
  key: string;
  currency: string;
  day: number;
  label: string;
  cohorts: number;
  players: number;
  revenue: number;
  ltv: number;
}

interface LtvPoint {
  day: string;
  currency: string;
  ltv: number;
  formatted: string;
}

type Money = (n: number) => string;

const istToday = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);

const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS);

const plural = (n: number, word: string) =>
  `${n.toLocaleString('en-IN')} ${word}${n === 1 ? '' : 's'}`;

// An unknown ISO code makes Intl throw; fall back to a plain number with the code in front.
const moneyFormatter = (currency: string): Money => {
  try {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency });
    return (n) => f.format(n);
  } catch {
    return (n) => `${currency} ${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }
};

/**
 * IAP lifetime value per player by days since signup.
 *
 * Rows arrive one per (signup day, currency, day index) with the cohort size
 * repeated on each, so cohorts are deduplicated by signup day before any sum.
 * A cohort nobody paid in still arrives as a single currency-less row and
 * counts toward the denominator — dropping it would turn LTV into ARPPU.
 *
 * Day N only includes cohorts old enough to have had N days, otherwise young
 * cohorts would drag later checkpoints down with revenue they could not have
 * earned yet.
 */
export const IapLtvCard: React.FC<{ params: GroupAnalyticsParams }> = ({ params }) => {
  const [rows, setRows] = useState<IapLtvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getIapLtv(params);
      setRows(res.data ?? []);
      setError(null);
    } catch (e) {
      setError(reportLoadErrorMessage(e, 'Could not load in-app purchase LTV'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = useMemo(() => {
    const today = istToday();
    const cohorts = new Map<string, Cohort>();
    const revenueByCurrency = new Map<string, number>();
    for (const r of rows) {
      if (!cohorts.has(r.cohortDay)) {
        cohorts.set(r.cohortDay, { size: r.cohortSize, age: daysBetween(r.cohortDay, today) });
      }
      if (r.currency) {
        revenueByCurrency.set(r.currency, (revenueByCurrency.get(r.currency) ?? 0) + r.revenue);
      }
    }
    let signups = 0;
    let oldestAge = -1;
    for (const c of cohorts.values()) {
      signups += c.size;
      oldestAge = Math.max(oldestAge, c.age);
    }
    const currencies = [...revenueByCurrency.keys()].sort();
    return { cohorts, revenueByCurrency, currencies, signups, oldestAge };
  }, [rows]);

  const formatters = useMemo(() => {
    const out: Record<string, Money> = {};
    for (const c of summary.currencies) out[c] = moneyFormatter(c);
    return out;
  }, [summary.currencies]);

  const checkpointRows = useMemo(() => {
    const out: CheckpointRow[] = [];
    for (const n of CHECKPOINTS) {
      const eligible = new Set<string>();
      let players = 0;
      for (const [day, c] of summary.cohorts) {
        if (c.age >= n) {
          eligible.add(day);
          players += c.size;
        }
      }
      // No cohort is old enough yet: skip rather than report a zero LTV.
      if (eligible.size === 0 || players === 0) continue;
      for (const currency of summary.currencies) {
        const revenue = rows
          .filter(
            (r) =>
              r.currency === currency &&
              r.dayIndex != null &&
              r.dayIndex <= n &&
              eligible.has(r.cohortDay)
          )
          .reduce((s, r) => s + r.revenue, 0);
        out.push({
          key: `${currency}-${n}`,
          currency,
          day: n,
          label: `D${n}`,
          cohorts: eligible.size,
          players,
          revenue,
          ltv: revenue / players,
        });
      }
    }
    // Currency-major order keeps each currency's checkpoints together in the table.
    return out.sort((a, b) => a.currency.localeCompare(b.currency) || a.day - b.day);
  }, [rows, summary]);

  const chartData = useMemo<LtvPoint[]>(
    () =>
      checkpointRows.map((r) => ({
        day: r.label,
        currency: r.currency,
        ltv: r.ltv,
        formatted: formatters[r.currency](r.ltv),
      })),
    [checkpointRows, formatters]
  );

  const firstSkipped = useMemo(
    () => CHECKPOINTS.find((n) => n > summary.oldestAge),
    [summary.oldestAge]
  );

  const multiCurrency = summary.currencies.length > 1;

  const yLabel = useMemo<Money>(
    () =>
      summary.currencies.length === 1
        ? formatters[summary.currencies[0]]
        : (n) => n.toLocaleString('en-IN', { maximumFractionDigits: 2 }),
    [summary.currencies, formatters]
  );

  const columns = useMemo<ColumnsType<CheckpointRow>>(
    () => [
      ...(multiCurrency
        ? [{ title: 'Currency', dataIndex: 'currency', key: 'currency' } as const]
        : []),
      {
        title: 'Day',
        dataIndex: 'label',
        key: 'day',
        sorter: (a, b) => a.day - b.day,
      },
      {
        title: 'Cohorts included',
        dataIndex: 'cohorts',
        key: 'cohorts',
        align: 'right',
        sorter: (a, b) => a.cohorts - b.cohorts,
        render: (v: number) => v.toLocaleString('en-IN'),
      },
      {
        title: 'Players included',
        dataIndex: 'players',
        key: 'players',
        align: 'right',
        sorter: (a, b) => a.players - b.players,
        render: (v: number) => v.toLocaleString('en-IN'),
      },
      {
        title: 'Revenue to date',
        dataIndex: 'revenue',
        key: 'revenue',
        align: 'right',
        sorter: (a, b) => a.revenue - b.revenue,
        render: (v: number, r) => formatters[r.currency](v),
      },
      {
        title: 'LTV per player',
        dataIndex: 'ltv',
        key: 'ltv',
        align: 'right',
        sorter: (a, b) => a.ltv - b.ltv,
        render: (v: number, r) => <strong>{formatters[r.currency](v)}</strong>,
      },
    ],
    [multiCurrency, formatters]
  );

  const hasCohorts = summary.cohorts.size > 0;
  const hasRevenue = summary.currencies.length > 0;

  return (
    <Card
      loading={loading}
      title={
        <span>
          LTV (in-app purchases){' '}
          <Tooltip title="Cohorts are players who signed up in the selected range, by IST signup day. Revenue is completed real purchases only; sandbox, tester and editor purchases are excluded. Each currency is shown on its own and never added to another. Day N only counts cohorts at least N days old, so later checkpoints use fewer cohorts.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
    >
      {error ? (
        <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} />
      ) : !hasCohorts ? (
        <Text type="secondary">
          No players signed up in this range. Cohorts are built from account creation dates (IST),
          and signups and purchases have been recorded since launch, so pick a range that contains
          signups.
        </Text>
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Statistic title="Signups in range" value={summary.signups.toLocaleString('en-IN')} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Signup days" value={summary.cohorts.size.toLocaleString('en-IN')} />
            </Col>
            {summary.currencies.map((c) => (
              <Col xs={12} md={6} key={c}>
                <Statistic
                  title={`Revenue to date (${c})`}
                  value={formatters[c](summary.revenueByCurrency.get(c) ?? 0)}
                />
              </Col>
            ))}
          </Row>

          {!hasRevenue ? (
            <Text type="secondary">
              None of these {plural(summary.signups, 'player')} has made a completed real purchase
              yet, so there is no revenue to spread across the cohort.
            </Text>
          ) : (
            <>
              <Table
                size="small"
                rowKey="key"
                dataSource={checkpointRows}
                columns={columns}
                pagination={checkpointRows.length > 15 ? { pageSize: 10, size: 'small' } : false}
              />
              {firstSkipped !== undefined && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
                  D{firstSkipped} and later need a cohort at least {plural(firstSkipped, 'day')}{' '}
                  old. The oldest cohort in this range is{' '}
                  {plural(Math.max(summary.oldestAge, 0), 'day')} old.
                </Text>
              )}

              <Text strong style={{ display: 'block', marginTop: 16, marginBottom: 8 }}>
                LTV per player by days since signup
              </Text>
              <Line
                data={chartData}
                xField="day"
                yField="ltv"
                colorField="currency"
                height={220}
                point={{ sizeField: 3 }}
                legend={multiCurrency ? { color: { position: 'top' } } : false}
                axis={{ y: { labelFormatter: (v: number) => yLabel(Number(v)) } }}
                tooltip={{
                  title: 'day',
                  items: [(d: LtvPoint) => ({ name: d.currency, value: d.formatted })],
                }}
              />
            </>
          )}
        </>
      )}
    </Card>
  );
};
